import { useState } from 'react';
import {
  Alert,
  AlertTitle,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  InputAdornment,
  Stack,
  TextField,
} from '@mui/material';
import { EmailOutlined, LockOutlined } from '@mui/icons-material';
import { useSetClientPinSelfMutation } from '@features/customer-reservations/queries';
import { IconBinocular } from '@shared/components/icons/icon-binocular';
import { FormattedMessage, useIntl } from 'react-intl';

const PRIMARY = '#D02964';

type Props = {
  open: boolean;
  /** Called after the password has been saved successfully */
  onSuccess?: () => void;
};

/**
 * Dialog, který stálého zákazníka po aktivaci vyzve k vytvoření hesla (PINu).
 * Nelze jej zavřít bez nastavení hesla – heslo je nutné pro příští přihlášení.
 */
const SetPinDialog = ({ open, onSuccess }: Props) => {
  const intl = useIntl();
  const [pin, setPin] = useState('');
  const [pinConfirm, setPinConfirm] = useState('');
  const [email, setEmail] = useState('');
  const [showPin, setShowPin] = useState(false);
  const [showPin2, setShowPin2] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const { mutate: savePin, isPending } = useSetClientPinSelfMutation();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setFormError(null);

    if (pin.length < 4) {
      setFormError(intl.formatMessage({ id: 'publicReservation.passwordTooShort' }));
      return;
    }
    if (pin !== pinConfirm) {
      setFormError(intl.formatMessage({ id: 'publicReservation.passwordsDoNotMatch' }));
      return;
    }

    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      setFormError(intl.formatMessage({ id: 'publicReservation.invalidResetEmail' }));
      return;
    }
    savePin(
      { pin, email: email.trim() },
      {
        onSuccess: () => {
          setPin('');
          setPinConfirm('');
          onSuccess?.();
        },
        onError: error => {
          setFormError(error.message);
        },
      },
    );
  };

  return (
    <Dialog open={open} maxWidth="xs" fullWidth disableEscapeKeyDown>
      <DialogTitle>
        <FormattedMessage id="publicReservation.createPassword" />
      </DialogTitle>
      <DialogContent>
        <Alert severity="warning" sx={{ mb: 3 }}>
          <AlertTitle sx={{ fontWeight: 700 }}>
            <FormattedMessage id="publicReservation.importantNotice" />
          </AlertTitle>
          <FormattedMessage id="publicReservation.passwordNotice" />
        </Alert>

        {formError && (
          <Alert severity="error" onClose={() => setFormError(null)} sx={{ mb: 2 }}>
            {formError}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <TextField
              label={intl.formatMessage({ id: 'publicReservation.resetEmail' })}
              value={email}
              onChange={e => setEmail(e.target.value)}
              type="email"
              autoComplete="email"
              required
              fullWidth
              disabled={isPending}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlined />
                    </InputAdornment>
                  ),
                },
              }}
            />
            <Stack spacing={2} direction="row" alignItems="center">
              <TextField
                label={intl.formatMessage({ id: 'publicReservation.passwordLabel' })}
                value={pin}
                onChange={e => setPin(e.target.value)}
                type={showPin ? 'text' : 'password'}
                autoComplete="new-password"
                fullWidth
                autoFocus
                disabled={isPending}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <Button
                variant="text"
                onClick={() => setShowPin(prev => !prev)}
                disabled={isPending}
                sx={{ minWidth: '32px' }}
              >
                <IconBinocular />
              </Button>
            </Stack>
            <Stack spacing={2} direction="row" alignItems="center">
              <TextField
                label={intl.formatMessage({ id: 'publicReservation.confirmPassword' })}
                type={showPin2 ? 'text' : 'password'}
                value={pinConfirm}
                onChange={e => setPinConfirm(e.target.value)}
                autoComplete="new-password"
                fullWidth
                disabled={isPending}
                slotProps={{
                  input: {
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlined />
                      </InputAdornment>
                    ),
                  },
                }}
              />
              <Button
                variant="text"
                onClick={() => setShowPin2(prev => !prev)}
                disabled={isPending}
                sx={{ minWidth: '32px' }}
              >
                <IconBinocular />
              </Button>
            </Stack>
            <Button
              type="submit"
              variant="contained"
              size="large"
              fullWidth
              disabled={isPending}
              startIcon={isPending ? <CircularProgress size={20} color="inherit" /> : undefined}
              sx={{ bgcolor: PRIMARY, '&:hover': { bgcolor: '#b0234f' }, borderRadius: '12px' }}
            >
              <FormattedMessage id="publicReservation.savePasswordAndContinue" />
            </Button>
          </Stack>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SetPinDialog;
